import { GetTokenHoldersResponseSchemaV1 } from "@/lib/friendtech";
import { getAddress, isAddress } from "ethers/lib/utils";
import { CID } from "ipfs-http-client";
import z from "zod";

export function formatAddressList(content: string) {
  if (!content) return null;

  const errors: Array<{ entry: string; line: number }> = [];
  const entries: string[] = [];
  const duplicates: Record<string, number> = {};

  const lines = content.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const trimmedLine = lines[i].trim();

    // Ignore empty lines
    if (!trimmedLine) {
      continue;
    }

    // Add broken addresses to list of errors
    if (!isAddress(trimmedLine)) {
      errors.push({
        entry: trimmedLine,
        line: i,
      });
      continue;
    }

    // Convert to checksummed address
    const address = getAddress(trimmedLine);

    // Count duplicate addresses
    if (entries.includes(address)) {
      duplicates[address] = (duplicates[address] ?? 1) + 1;
    }

    // Add address to list
    entries.push(address);
  }

  return {
    valid: !errors.length,
    errors,
    entries,
    duplicates: Object.keys(duplicates).length ? duplicates : null,
  };
}

export function formatEntriesList(
  content: string,
  entriesType: "address" | "string"
) {
  if (!content) return null;

  if (entriesType === "address") {
    return formatAddressList(content);
  }

  const entries: string[] = [];
  const duplicates: Record<string, number> = {};

  const lines = content.split("\n");

  for (const line of lines) {
    // Normalize entry
    const entry = normalizeEntry(line);

    // Ignore empty entries
    if (!entry) {
      continue;
    }

    // Count duplicate entries
    if (entries.includes(entry)) {
      duplicates[entry] = (duplicates[entry] ?? 1) + 1;
    }

    // Add entry to list
    entries.push(entry);
  }

  return {
    valid: true,
    errors: [],
    entries,
    duplicates: Object.keys(duplicates).length ? duplicates : null,
  };
}

export function makeIPFSURL(cid: string | CID) {
  return `https://cloudflare-ipfs.com/ipfs/${cid.toString()}`;
}

export const FriendTechUserMappingSchema = z.object({
  version: z.literal("1"), // FT API probs not stable
  users: GetTokenHoldersResponseSchemaV1.shape.users,
});

export const RaffleMetadataSchema = z.object({
  name: z.string(),
  winners: z.number(),
  entries: z.array(z.string()),
  entriesType: z.union([z.literal("address"), z.literal("string")]),
  minBlocksToWait: z.number(),
  friendTechMappings: FriendTechUserMappingSchema.optional(),
});

export type RaffleMetadata = z.infer<typeof RaffleMetadataSchema>;

export function normalizeEntry(entry: string) {
  return entry.trim().toLowerCase();
}
